import { Component, inject, Output } from '@angular/core';
import { AuthApiService } from '../../../core/services/auth/auth-api.service';
import { Router } from '@angular/router';
import { FormBuilder, FormControl, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { EMAIL_REGEX, GITHUB_AUTH_URL, MIN_PASSWORD_LENGTH, PASSWORD_REGEX } from '../constant/auth.constant';
import { NgClass, NgOptimizedImage } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { EventEmitter } from '@angular/core';
import { passwordConfirmationValidator } from '../../../core/validator/confirmPasswordValidator';
import { take } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-user-register',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, NgClass, NgOptimizedImage],
  templateUrl: './user-register.component.html',
  styleUrl: '../user-authentification.component.scss',
})
export class UserRegisterComponent {
  @Output() registrationSuccess = new EventEmitter<void>();

  readonly MAX_TAKE_COUNT = 1;

  private _authService = inject(AuthApiService);
  private _router = inject(Router);
  private _toast = inject(ToastrService);
  private _fb: NonNullableFormBuilder = inject(FormBuilder).nonNullable;

  isSubmitted = false;
  isLoading = false;

  public passwordVisible = false;
  public confirmPasswordVisible = false;

  RegisterForm = this._fb.group(
    {
      email: ['', [Validators.required, Validators.pattern(EMAIL_REGEX)]],
      password: ['', [Validators.required, Validators.minLength(MIN_PASSWORD_LENGTH), Validators.pattern(PASSWORD_REGEX)]],
      confirmPassword: ['', Validators.required],
      rgpdConsent: [false, Validators.requiredTrue],
    },
    {
      validators: passwordConfirmationValidator('password', 'confirmPassword'),
    }
  );

  get passwordMismatch(): boolean {
    return this.RegisterForm.get('confirmPassword')?.hasError('passwordMismatch') ?? false;
  }

  get email(): FormControl<string> {
    return this.RegisterForm.controls.email;
  }

  get password(): FormControl<string> {
    return this.RegisterForm.controls.password;
  }

  get confirmPassword(): FormControl<string> {
    return this.RegisterForm.controls.confirmPassword;
  }

  get rgpdConsent(): FormControl<boolean> {
    return this.RegisterForm.controls.rgpdConsent;
  }

  public togglePasswordVisibility(field: 'password' | 'confirmPassword'): void {
    if (field === 'password') {
      this.passwordVisible = !this.passwordVisible;
    } else if (field === 'confirmPassword') {
      this.confirmPasswordVisible = !this.confirmPasswordVisible;
    }
  }

  register(): void {
    this.isSubmitted = true;

    if (this.RegisterForm.invalid) return;

    const { email, password } = this.RegisterForm.value;
    if (!email || !password) return;

    this.isLoading = true;

    this._authService
      .register$({ email, password })
      .pipe(take(this.MAX_TAKE_COUNT))
      .subscribe({
        next: () => {
          this.isLoading = false;
          this._loginAfterRegister(email, password);
        },
        error: err => {
          this.isLoading = false;
          this._handleRegisterError(err);
        },
      });
  }

  private _loginAfterRegister(email: string, password: string): void {
    this._authService
      .login$({ email, password })
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.registrationSuccess.emit();
          this._router.navigate(['']);
          this._toast.success('Inscription et connexion réussies !', 'Succès');
          this.RegisterForm.reset();
          this.isSubmitted = false;
        },
        error: () => {
          this._toast.error('Inscription réussie, mais connexion échouée.', 'Erreur');
        },
      });
  }

  private _handleRegisterError(err: HttpErrorResponse): void {
    if (err.message === 'Cet email est déjà utilisé.') {
      this._toast.error('Cet email est déjà utilisé. Merci d’en choisir un autre.', 'Erreur');
      this.email?.setErrors({ emailTaken: true });
    } else {
      this._toast.error("Une erreur s'est produite pendant l'inscription", 'Erreur');
    }
  }

  loginWithGitHub(): void {
    window.location.href = GITHUB_AUTH_URL;
  }
}
